import React, { useCallback, useState } from "react";
import { Alert } from "@jugl-web/ui-kit";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useTwilioAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/TwilioAccountProvider/useTwilioAccount";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { useManageAccountsDrawer } from "../../../../providers/ManageAccountsDrawerContext";
import { ReactComponent as TwilioLogoIcon } from "../../assets/twilio-logo.svg";
import { ReactComponent as RemoveIcon } from "../../assets/remove.svg";

const maskSid = (sid: string) => {
  if (sid.length <= 8) {
    return sid;
  }
  return `${sid.slice(0, 4)}${"*".repeat(8)}${sid.slice(-4)}`;
};

export const TwilioAccountHeaderCard: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const {
    twilioAccount,
    disconnectTwilioAccount,
    isDisconnectingTwilioAccount,
  } = useTwilioAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const handleDisconnectAccount = useCallback(async () => {
    try {
      await disconnectTwilioAccount();
      toast(
        t({
          id: "client-support-page.twilio-account-disconnected",
          defaultMessage: "Twilio account disconnected successfully",
        })
      );
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    disconnectTwilioAccount,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
    toast,
  ]);

  return (
    <>
      <CRMAccountCard
        icon={TwilioLogoIcon}
        title={t({ id: "common.twilio", defaultMessage: "Twilio" })}
        description={twilioAccount ? maskSid(twilioAccount.sid) : ""}
        status="connected"
        menuItems={[
          [
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: () => setIsDisconnectAlertOpen(true),
              isDisabled: isDisconnectingTwilioAccount,
            },
          ],
        ]}
      />
      <Alert
        isOpen={isDisconnectAlertOpen}
        onRequestClose={() => setIsDisconnectAlertOpen(false)}
        title={t({
          id: "client-support-page.disconnect-account",
          defaultMessage: "Disconnect Account",
        })}
        content={t({
          id: "client-support-page.disconnect-twilio-account",
          defaultMessage:
            "Are you sure you want to disconnect your Twilio account?",
        })}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "client-support-page.disconnect",
              defaultMessage: "Disconnect",
            }),
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await handleDisconnectAccount();
            },
          },
        ]}
      />
    </>
  );
};
